/**
 * AdminJobsInventoryPage.js — /admin/jobs  (RC 1.30.A.1)
 *
 * Utilitarian operator surface over the real jobs database. Reads
 *   GET /api/admin/jobs/inventory      — aggregate counts
 *   GET /api/admin/jobs/list           — paginated jobs list
 *
 * Both endpoints are `require_admin` on the backend; non-admin
 * callers get a 403 surfaced here as a plain "Admin only" state.
 * This is an operator view — intentionally plain, no marketing UI.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Loader2, RefreshCw, ShieldAlert, Database, Eye, EyeOff,
  ChevronLeft, ChevronRight,
} from 'lucide-react';

const HIDDEN_OPTIONS = [
  { value: 'visible', label: 'Visible (default)' },
  { value: 'stale',   label: 'Hidden by staleness' },
  { value: '404',     label: 'Hidden by source 404' },
  { value: 'any',     label: 'Any (all rows)' },
];

const SOURCE_KIND_OPTIONS = [
  { value: '__all__',     label: 'All source kinds' },
  { value: 'platform',    label: 'Platform (adapters)' },
  { value: 'user_import', label: 'User import' },
];

function fmtInt(n) {
  if (typeof n !== 'number') return '—';
  return n.toLocaleString();
}

function fmtTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: '2-digit', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function StatCard({ label, value, testId, tone }) {
  const toneCls = {
    default: 'text-ndor-ink',
    warn:    'text-amber-200',
    bad:     'text-rose-200',
    ok:      'text-emerald-200',
  }[tone || 'default'];
  return (
    <Card
      className="border-ndor-edge shadow-sm"
      data-testid={testId}
    >
      <CardHeader className="pb-1">
        <CardTitle className="text-xs font-medium uppercase tracking-wider text-ndor-ink-muted">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className={`text-3xl font-mono tabular-nums ${toneCls}`}>
          {fmtInt(value)}
        </p>
      </CardContent>
    </Card>
  );
}

export default function AdminJobsInventoryPage() {
  const [forbidden, setForbidden]   = useState(false);
  const [inventory, setInventory]   = useState(null);
  const [invLoading, setInvLoading] = useState(true);
  const [invError, setInvError]     = useState(null);

  const [list, setList]             = useState(null);
  const [listLoading, setListLoad]  = useState(true);
  const [listError, setListError]   = useState(null);

  const [page, setPage]             = useState(1);
  const [pageSize]                  = useState(50);
  const [q, setQ]                   = useState('');
  const [qBuffer, setQBuffer]       = useState('');
  const [sourceKind, setSourceKind] = useState('__all__');
  const [hidden, setHidden]         = useState('visible');

  const loadInventory = useCallback(async () => {
    setInvLoading(true);
    setInvError(null);
    try {
      const data = await apiFetch('/api/admin/jobs/inventory?top_companies=20');
      setInventory(data || null);
    } catch (err) {
      if (err?.status === 403) setForbidden(true);
      else setInvError(err?.message || 'Failed to load inventory.');
    } finally {
      setInvLoading(false);
    }
  }, []);

  const loadList = useCallback(async () => {
    setListLoad(true);
    setListError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        hidden,
      });
      if (q.trim()) params.set('q', q.trim());
      if (sourceKind && sourceKind !== '__all__') params.set('source_kind', sourceKind);
      const data = await apiFetch(`/api/admin/jobs/list?${params.toString()}`);
      setList(data || null);
    } catch (err) {
      if (err?.status === 403) setForbidden(true);
      else setListError(err?.message || 'Failed to load jobs list.');
    } finally {
      setListLoad(false);
    }
  }, [page, pageSize, q, sourceKind, hidden]);

  useEffect(() => { loadInventory(); }, [loadInventory]);
  useEffect(() => { loadList(); }, [loadList]);

  // Reset to page 1 whenever a filter changes.
  useEffect(() => { setPage(1); }, [q, sourceKind, hidden]);

  const totals = inventory?.totals || {};
  const byKind = inventory?.by_source_kind || {};
  const byCo   = inventory?.by_company_top || [];

  const kindRows = useMemo(() => {
    return Object.entries(byKind)
      .sort((a, b) => b[1] - a[1]);
  }, [byKind]);

  if (forbidden) {
    return (
      <div
        data-testid="admin-jobs-forbidden"
        className="mx-auto max-w-xl py-16 px-4 text-center"
      >
        <ShieldAlert className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <h1 className="text-xl font-semibold text-ndor-ink">Admin only</h1>
        <p className="mt-2 text-sm text-ndor-ink-muted">
          You need operator access to view the jobs inventory.
        </p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil((list?.total || 0) / pageSize));

  return (
    <div
      className="mx-auto max-w-[1400px] px-4 py-8 space-y-8"
      data-testid="admin-jobs-inventory-page"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-ndor-ink flex items-center gap-2">
            <Database className="h-5 w-5 text-ndor-ink-muted" />
            Jobs Inventory
          </h1>
          <p className="mt-1 text-sm text-ndor-ink-muted">
            Operator view of the real jobs database. Counts are live —
            refresh to re-read.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { loadInventory(); loadList(); }}
          disabled={invLoading || listLoading}
          data-testid="admin-jobs-refresh"
        >
          {(invLoading || listLoading)
            ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            : <RefreshCw className="h-4 w-4 mr-1.5" />}
          Refresh
        </Button>
      </div>

      {/* Totals */}
      {invError && (
        <div
          className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 text-sm px-3 py-2"
          data-testid="admin-jobs-inventory-error"
        >
          {invError}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total jobs"     value={totals.total}                testId="admin-jobs-stat-total" />
        <StatCard label="Visible"        value={totals.visible}              testId="admin-jobs-stat-visible"        tone="ok"   />
        <StatCard label="Hidden · stale" value={totals.hidden_by_staleness}  testId="admin-jobs-stat-hidden-stale"   tone="warn" />
        <StatCard label="Hidden · 404"   value={totals.hidden_by_source_404} testId="admin-jobs-stat-hidden-404"     tone="bad"  />
      </div>

      {/* By source-kind + by company, side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-ndor-edge shadow-sm" data-testid="admin-jobs-by-source-kind">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-ndor-ink">
              By source kind
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invLoading && (
              <div className="py-6 flex items-center justify-center text-ndor-ink-muted">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
            {!invLoading && kindRows.length === 0 && (
              <p className="text-sm text-ndor-ink-muted py-4">No jobs indexed yet.</p>
            )}
            {!invLoading && kindRows.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source kind</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {kindRows.map(([kind, count]) => (
                    <TableRow key={kind} data-testid={`admin-jobs-kind-row-${kind}`}>
                      <TableCell className="font-mono text-xs">{kind}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtInt(count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="border-ndor-edge shadow-sm" data-testid="admin-jobs-by-company-top">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-ndor-ink">
              Top 20 companies (visible)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invLoading && (
              <div className="py-6 flex items-center justify-center text-ndor-ink-muted">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
            {!invLoading && byCo.length === 0 && (
              <p className="text-sm text-ndor-ink-muted py-4">No companies to report.</p>
            )}
            {!invLoading && byCo.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead className="text-right">Visible jobs</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byCo.map((row, i) => (
                    <TableRow key={`${row.company}-${i}`} data-testid={`admin-jobs-company-row-${i}`}>
                      <TableCell>{row.company}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtInt(row.count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Jobs list */}
      <Card className="border-ndor-edge shadow-sm" data-testid="admin-jobs-list-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-ndor-ink">
            Jobs list
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => { e.preventDefault(); setQ(qBuffer); }}
            >
              <Input
                placeholder="Search title or company…"
                value={qBuffer}
                onChange={(e) => setQBuffer(e.target.value)}
                className="h-9 w-64"
                data-testid="admin-jobs-search-input"
              />
              <Button
                type="submit" size="sm" variant="outline"
                data-testid="admin-jobs-search-submit"
              >
                Search
              </Button>
              {q && (
                <Button
                  type="button" size="sm" variant="ghost"
                  onClick={() => { setQBuffer(''); setQ(''); }}
                  data-testid="admin-jobs-search-clear"
                >
                  Clear
                </Button>
              )}
            </form>

            <Select value={sourceKind} onValueChange={setSourceKind}>
              <SelectTrigger className="h-9 w-[200px]" data-testid="admin-jobs-filter-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_KIND_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={hidden} onValueChange={setHidden}>
              <SelectTrigger className="h-9 w-[220px]" data-testid="admin-jobs-filter-hidden">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HIDDEN_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {listError && (
            <div
              className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 text-sm px-3 py-2 mb-3"
              data-testid="admin-jobs-list-error"
            >
              {listError}
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listLoading && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-6">
                      <div className="flex items-center justify-center text-ndor-ink-muted">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {!listLoading && (list?.items?.length || 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-6 text-center text-sm text-ndor-ink-muted" data-testid="admin-jobs-list-empty">
                      {(q || sourceKind !== '__all__' || hidden !== 'visible')
                        ? 'No jobs match this filter.'
                        : 'No jobs found.'}
                    </TableCell>
                  </TableRow>
                )}
                {!listLoading && (list?.items || []).map((row) => {
                  const hiddenStale = Boolean(row.hidden_by_staleness);
                  const hidden404   = Boolean(row.hidden_by_source_404);
                  return (
                    <TableRow key={row.id} data-testid={`admin-jobs-row-${row.id}`}>
                      <TableCell className="max-w-[300px]">
                        <a
                          href={row.url || '#'}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-ndor-ink hover:text-ndor-gold truncate block"
                          title={row.title}
                        >
                          {row.title || <span className="text-ndor-ink-muted italic">(untitled)</span>}
                        </a>
                      </TableCell>
                      <TableCell className="max-w-[180px] truncate" title={row.company || ''}>
                        {row.company || <span className="text-ndor-ink-muted">—</span>}
                      </TableCell>
                      <TableCell className="max-w-[180px] truncate" title={row.location || ''}>
                        {row.location || <span className="text-ndor-ink-muted">—</span>}
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-xs text-ndor-ink-muted">{row.source_kind || '—'}</span>
                      </TableCell>
                      <TableCell>
                        {hiddenStale && (
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-200 border-amber-500/40 mr-1 text-[11px]">
                            <EyeOff className="h-3 w-3 mr-1" /> stale
                          </Badge>
                        )}
                        {hidden404 && (
                          <Badge variant="outline" className="bg-rose-500/10 text-rose-200 border-rose-500/40 text-[11px]">
                            <EyeOff className="h-3 w-3 mr-1" /> 404
                          </Badge>
                        )}
                        {!hiddenStale && !hidden404 && (
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-200 border-emerald-500/40 text-[11px]">
                            <Eye className="h-3 w-3 mr-1" /> visible
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-ndor-ink-muted">
                        {fmtTs(row.created_at)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-ndor-ink-muted">
                        {fmtTs(row.last_seen_at)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div
            className="flex items-center justify-between mt-3 text-xs text-ndor-ink-muted"
            data-testid="admin-jobs-pagination"
          >
            <span>
              {list
                ? `Showing page ${list.page || page} of ${totalPages} · ${fmtInt(list.total || 0)} total`
                : '—'}
            </span>
            <div className="flex items-center gap-1">
              <Button
                size="sm" variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || listLoading}
                data-testid="admin-jobs-page-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => setPage((p) => p + 1)}
                disabled={!list?.has_more || listLoading}
                data-testid="admin-jobs-page-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
